<div class="max-w-lg mx-auto bg-white rounded-lg">
  <div class="mb-6">
    <div class="flex items-center mb-4">
      <div class="w-2 h-8 rounded mr-3" [ngClass]="readonly ? 'bg-gray-400' : 'bg-blue-500'"></div>
      <h2 class="text-2xl font-bold text-gray-800">{{ getModalTitle() }}</h2>
      <!-- Badge modo solo lectura -->
      <span *ngIf="readonly" class="ml-3 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
        <i class="fas fa-eye mr-1"></i> Solo lectura
      </span>
    </div>
    <p class="text-gray-600 text-sm">{{ getModalDescription() }}</p>
  </div>

  <form [formGroup]="budgetCategoryForm" (ngSubmit)="onSubmit()">
    <div class="mb-6">
      <label for="budgetType" class="block text-sm font-semibold text-gray-700 mb-2">
        <span class="flex items-center">
          <span *ngIf="!readonly" class="text-red-500 mr-1">*</span>
          Tipo de Categoría
        </span>
      </label>
      <p-dropdown 
        id="budgetType"
        formControlName="budgetType"
        [options]="budgetTypes"
        placeholder="Seleccione el tipo de categoría"
        class="w-full custom-dropdown"
        [showClear]="!readonly"
        [disabled]="readonly">
      </p-dropdown>
      <div *ngIf="!readonly && budgetCategoryForm.get('budgetType')?.invalid && budgetCategoryForm.get('budgetType')?.touched" 
           class="text-red-500 text-sm mt-1 flex items-center">
        <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
        </svg>
        El tipo de categoría es requerido.
      </div>
      <p *ngIf="!readonly" class="text-xs text-gray-500 mt-1">Seleccione si es una categoría de ingresos o gastos</p>
    </div>
    <div class="mb-6">
      <label for="name" class="block text-sm font-semibold text-gray-700 mb-2">
        <span class="flex items-center">
          <span *ngIf="!readonly" class="text-red-500 mr-1">*</span>
          Nombre de la Categoría
        </span>
      </label>
      <input 
        id="name" 
        type="text" 
        formControlName="name" 
        [readonly]="readonly"
        class="w-full px-3 py-2 border border-gray-300 rounded-md transition-colors"
        [ngClass]="readonly ? 'bg-gray-50 cursor-not-allowed' : 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'"
        placeholder="Ej: Ingresos por servicios, Gastos operacionales, etc." />
      <div *ngIf="!readonly && budgetCategoryForm.get('name')?.invalid && budgetCategoryForm.get('name')?.touched" 
           class="text-red-500 text-sm mt-1 flex items-center">
        <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
        </svg>
        El nombre de la categoría es requerido.
      </div>
      <p *ngIf="!readonly" class="text-xs text-gray-500 mt-1">Ingrese un nombre descriptivo para la categoría</p>
    </div>

    <div class="mb-6">
      <label class="flex items-center" [ngClass]="readonly ? 'cursor-default' : 'cursor-pointer'">
        <input 
          type="checkbox" 
          formControlName="active" 
          [disabled]="readonly"
          class="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
          [ngClass]="readonly ? 'cursor-not-allowed' : ''" />
        <span class="text-sm font-semibold text-gray-700">
          Categoría Activa
        </span>
      </label>
      <p *ngIf="!readonly" class="text-xs text-gray-500 mt-1 ml-7">Las categorías inactivas no aparecerán en los formularios</p>
    </div>
    
    <div class="flex justify-end space-x-3 pt-6 border-t border-gray-200">
      <!-- Botón Cerrar (solo en modo lectura) -->
      <button 
        *ngIf="readonly"
        type="button" 
        (click)="closeModal.emit()"
        class="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-200 bg-gray-500 text-white hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
        Cerrar
      </button>
      
      <!-- Botón Guardar (modo edición/creación) -->
      <button 
        *ngIf="!readonly"
        type="submit" 
        [disabled]="budgetCategoryForm.invalid"
        class="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2"
        [ngClass]="{
          'bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-500 transform hover:scale-105': !budgetCategoryForm.invalid,
          'bg-gray-300 text-gray-500 cursor-not-allowed': budgetCategoryForm.invalid
        }">
        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
        </svg>
        {{ categoryId ? 'Actualizar Categoría' : 'Crear Categoría' }}
      </button>
    </div>
  </form>
</div>
  